import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { isClickedOutside, lockScroll, onAnimationEnd, unlockScroll } from '@theme/utilities';

/** Viewport width below which the drawer opens as a modal overlay (no squeeze). */
const MODAL_BREAKPOINT = 990;

/**
 * A drawer that opens from the right side.
 *
 * On wide viewports (≥ 990px) the drawer squeezes page content alongside it.
 * The panel is a non-modal dialog (`show()`); we install a focus trap via
 * `trapFocus()` so Tab cycles within the drawer, mirroring the modal-mode
 * a11y contract. Focus moves to the close button on open and returns to
 * the trigger on close.
 *
 * On narrow viewports (< 990px) the drawer overlays with a backdrop. The
 * panel is a modal dialog (`showModal()`) — native focus trap, scroll-lock,
 * and ARIA semantics. Same focus-on-close-button + restore-on-close UX.
 *
 * Dispatches {@link DrawerOpenEvent} and {@link DrawerCloseEvent}.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} panel - The dialog that owns all visual styles and animation.
 *
 * @extends {Component<Refs>}
 */
export class ThemeDrawer extends Component {
  requiredRefs = ['panel'];

  /** Monotonically increasing counter for z-index stacking of multiple open drawers. */
  static #stackOrder = 0;

  /** @type {boolean} */
  #isClosing = false;

  /** When true, the drawer will reopen after the current close animation finishes. */
  /** @type {{ enter?: 'inline-start' } | false} */
  #deferredOpen = false;

  /** Element to restore focus to when the drawer closes. */
  /** @type {HTMLElement | null} */
  #previouslyFocused = null;

  /** @type {MediaQueryList} */
  #modalQuery = window.matchMedia(`(max-width: ${MODAL_BREAKPOINT - 1}px)`);

  /**
   * @returns {boolean} Whether the drawer is currently open.
   */
  get isOpen() {
    return this.hasAttribute('open');
  }

  connectedCallback() {
    super.connectedCallback();
    this.#modalQuery.addEventListener('change', this.#onModalBreakpointChange);

    // Sync the static stack counter with any --drawer-stack-order set by the
    // synchronous restore script (which runs before this module loads).
    const restoredOrder = Number(this.style.getPropertyValue('--drawer-stack-order') || 0);
    if (restoredOrder > ThemeDrawer.#stackOrder) {
      ThemeDrawer.#stackOrder = restoredOrder;
    }

    if (this.isOpen) {
      this.#onRestore();
    }
  }

  /**
   * Restore path: the inline script in theme-drawer.liquid set [open] on
   * <theme-drawer> + <dialog> before this module loaded. The dialog is
   * already visible — we just wire close listeners. We deliberately skip
   * trapFocus and focus moves: the user is loading a fresh page and
   * expects focus on main content, not inside a drawer left over from
   * the previous session.
   */
  #onRestore() {
    const { panel } = this.refs;
    if (this.#modalQuery.matches) {
      lockScroll(panel);
    }

    document.addEventListener('keydown', this.#onKeyDown);
    panel.addEventListener('click', this.#onBackdropClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    unlockScroll(this.refs.panel);
    this.#modalQuery.removeEventListener('change', this.#onModalBreakpointChange);
    this.#removeEventListeners();
    removeTrapFocus();
    this.#deferredOpen = false;
  }

  /**
   * Closes the drawer when the user clicks the backdrop (outside the dialog
   * content area). Only meaningful in modal mode — in sidebar mode the
   * dialog has no backdrop so this handler is inert.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #onBackdropClick = (event) => {
    const { panel } = this.refs;

    if (isClickedOutside(event, panel)) {
      if (this.#hasOpenNestedDialog()) return;

      this.close();
    }
  };

  /**
   * @returns {boolean} Whether the drawer panel contains an open dialog other than itself.
   */
  #hasOpenNestedDialog() {
    return this.#getOpenNestedDialog() !== null;
  }

  /**
   * @returns {HTMLDialogElement | null} The open dialog nested inside the drawer panel, if any.
   */
  #getOpenNestedDialog() {
    return /** @type {HTMLDialogElement | null} */ (this.refs.panel.querySelector('dialog[open]'));
  }

  /**
   * Switches the dialog between modal and non-modal when the viewport
   * crosses the modal breakpoint while the drawer is open.
   */
  #onModalBreakpointChange = () => {
    if (!this.isOpen) return;

    const { panel } = this.refs;
    const nestedDialog = this.#getOpenNestedDialog();
    const nestedActiveElement =
      nestedDialog?.contains(document.activeElement) && document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;

    // Close the current dialog mode and immediately reopen in the new mode.
    // No animation — the drawer stays visually in place.
    panel.close();
    removeTrapFocus();

    if (this.#modalQuery.matches) {
      lockScroll(panel);
      panel.showModal();
    } else {
      unlockScroll(panel);
      panel.show();
      trapFocus(panel);
    }

    if (nestedDialog?.open) {
      this.#bringNestedDialogToFront(nestedDialog, nestedActiveElement);
    } else {
      this.#focusInitialElement();
    }

    // Ensure keydown and click listeners are registered. The sessionStorage
    // restore path bypasses open(), so these may not have been added yet.
    // addEventListener deduplicates identical listeners, so this is safe
    // even if they were already registered.
    document.addEventListener('keydown', this.#onKeyDown);
    panel.addEventListener('click', this.#onBackdropClick);
  };

  /**
   * Closes the drawer when the user presses Escape.
   * preventDefault() suppresses the native dialog cancel event in modal mode.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #onKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    // isComposing: don't close while an Input Method Editor (e.g. Japanese,
    // Chinese, Korean input) consumes Escape to dismiss its candidate list —
    // can happen inside the cart's discount-code field.
    // defaultPrevented: let nested components handle Escape first.
    if (event.isComposing || event.defaultPrevented) return;
    if (!this.isOpen) return;
    if (!this.#isTopmost()) return;

    event.preventDefault();
    this.close();
  };

  /**
   * Shows the drawer.
   *
   * @param {object} [options]
   * @param {'inline-start'} [options.enter] - Slide direction. `'inline-start'` slides left-to-right (used when the chat docks).
   */
  open({ enter } = {}) {
    if (this.isOpen) {
      this.#bringToFront({ enter });
      return;
    }
    if (this.#isClosing) {
      this.#deferredOpen = { enter };
      return;
    }

    const { panel } = this.refs;

    ThemeDrawer.#stackOrder += 1;
    this.style.setProperty('--drawer-stack-order', String(ThemeDrawer.#stackOrder));

    const openClass =
      enter === 'inline-start' ? 'theme-drawer__dialog--opening-inline-start' : 'theme-drawer__dialog--opening';
    panel.classList.add(openClass);

    this.#previouslyFocused = /** @type {HTMLElement | null} */ (document.activeElement);

    if (this.#modalQuery.matches) {
      lockScroll(panel);
      panel.showModal();
    } else {
      panel.show();
      trapFocus(panel);
    }

    this.#focusInitialElement();

    onAnimationEnd(panel, () => panel.classList.remove(openClass), { subtree: false });

    this.setAttribute('open', '');
    this.dispatchEvent(new DrawerOpenEvent());

    document.addEventListener('keydown', this.#onKeyDown);
    panel.addEventListener('click', this.#onBackdropClick);
  }

  /**
   * Bumps this drawer's z-index to the top of the stack and replays
   * the slide-in animation. Called when open() is invoked on an
   * already-open drawer.
   *
   * @param {object} [options]
   * @param {'inline-start'} [options.enter] - Slide direction.
   */
  #bringToFront({ enter } = {}) {
    const { panel } = this.refs;

    ThemeDrawer.#stackOrder += 1;
    this.style.setProperty('--drawer-stack-order', String(ThemeDrawer.#stackOrder));

    // In modal mode, dialogs live in the browser's top layer where z-index
    // is ignored — stacking follows showModal() call order. Re-calling
    // showModal() moves this dialog to the top of the stack.
    if (this.#modalQuery.matches && panel.open) {
      lockScroll(panel);
      panel.close();
      panel.showModal();
    }

    const openClass =
      enter === 'inline-start' ? 'theme-drawer__dialog--opening-inline-start' : 'theme-drawer__dialog--opening';
    panel.classList.add(openClass);
    onAnimationEnd(panel, () => panel.classList.remove(openClass), { subtree: false });

    // Re-fire so persistDrawerState picks up the new stack order.
    this.dispatchEvent(new DrawerOpenEvent());
  }

  #removeEventListeners() {
    const { panel } = this.refs;

    document.removeEventListener('keydown', this.#onKeyDown);
    panel.removeEventListener('click', this.#onBackdropClick);
  }

  /**
   * Moves focus to the close button inside the drawer panel.
   * Every theme-drawer renders the close button via theme-drawer-header.
   * If a future drawer omits it, that drawer is responsible for its own focus management.
   */
  #focusInitialElement() {
    const closeButton = /** @type {HTMLElement | null} */ (
      this.refs.panel.querySelector('.theme-drawer__close-button')
    );
    closeButton?.focus();
  }

  /**
   * Reopens a nested modal after the drawer panel changes mode so it remains
   * above the drawer in the browser top layer.
   *
   * @param {HTMLDialogElement} dialog - The nested dialog to restack.
   * @param {HTMLElement | null} focusTarget - Element to refocus inside the nested dialog.
   */
  #bringNestedDialogToFront(dialog, focusTarget) {
    dialog.close();
    dialog.showModal();

    if (focusTarget && document.contains(focusTarget) && dialog.contains(focusTarget)) {
      focusTarget.focus();
    }
  }

  /**
   * Slides the drawer out, waits for the animation, then closes the dialog.
   */
  async #closeDialog() {
    if (!this.isOpen) return;
    if (this.#isClosing) return;
    this.#isClosing = true;

    this.#removeEventListeners();
    removeTrapFocus();

    const { panel } = this.refs;

    this.removeAttribute('open');
    this.dispatchEvent(new DrawerCloseEvent());

    unlockScroll(panel);

    if (panel.open) {
      // Cancel any in-progress open animation before starting the close.
      panel.classList.remove('theme-drawer__dialog--opening', 'theme-drawer__dialog--opening-inline-start');

      panel.classList.add('theme-drawer__dialog--closing');
      await onAnimationEnd(panel, undefined, { subtree: false });
      panel.classList.remove('theme-drawer__dialog--closing');
    }

    panel.close();
    this.style.removeProperty('--drawer-stack-order');

    if (!document.querySelector('theme-drawer[open]')) {
      ThemeDrawer.#stackOrder = 0;
    }

    const trigger = this.#previouslyFocused;
    this.#previouslyFocused = null;
    // A Section Rendering API morph between open and close can replace the
    // trigger node. The JS reference stays valid but the element is detached
    // from the live DOM — and .focus() on a detached node is a silent no-op,
    // so we explicitly check and fall back to a fresh query.
    if (trigger && document.contains(trigger)) {
      trigger.focus();
    } else {
      /** @type {HTMLElement | null} */ (document.querySelector(`[aria-controls="${this.id}"]`))?.focus();
    }

    this.#isClosing = false;

    // Reconcile: if open() was called during the close animation, reopen now.
    if (this.#deferredOpen) {
      const options = this.#deferredOpen;
      this.#deferredOpen = false;
      this.open(options);
    }
  }

  /**
   * Closes the drawer.
   */
  async close() {
    if (this.#isClosing) {
      // A close is already in progress — cancel any deferred open so the
      // drawer stays closed when the animation finishes.
      this.#deferredOpen = false;
      return;
    }
    await this.#closeDialog();
  }

  /**
   * Toggles the drawer. If the drawer is open but not the topmost in the
   * stack, it is brought to front instead of closed.
   */
  toggle() {
    if (this.isOpen && this.#isTopmost()) {
      return this.close();
    }
    return this.open();
  }

  /**
   * @returns {boolean} Whether this drawer has the highest stack order among all open drawers.
   */
  #isTopmost() {
    const myOrder = Number(this.style.getPropertyValue('--drawer-stack-order') || 0);
    for (const other of document.querySelectorAll('theme-drawer[open]')) {
      if (other === this) continue;
      const otherOrder = Number(/** @type {HTMLElement} */ (other).style.getPropertyValue('--drawer-stack-order') || 0);
      if (otherOrder > myOrder) return false;
    }
    return true;
  }
}

if (!customElements.get('theme-drawer')) {
  customElements.define('theme-drawer', ThemeDrawer);
}

export class DrawerOpenEvent extends CustomEvent {
  constructor() {
    super(DrawerOpenEvent.eventName, { bubbles: true });
  }

  static eventName = 'theme-drawer:open';
}

export class DrawerCloseEvent extends CustomEvent {
  constructor() {
    super(DrawerCloseEvent.eventName, { bubbles: true });
  }

  static eventName = 'theme-drawer:close';
}

export class DrSaviourWishlistStore {
  constructor(getStorage) {
    this.getStorage = getStorage;
    this.memory = [];
    this.persistent = true;
    this.memoryOnly = false;
  }

  valid(handle) {
    return typeof handle === 'string' && /^[a-z0-9][a-z0-9-]{0,254}$/.test(handle);
  }

  read() {
    if (this.memoryOnly) return [...this.memory];
    let raw;
    try {
      raw = this.getStorage().getItem('drsaviour:wishlist:v1');
      this.persistent = true;
    } catch {
      this.persistent = false;
      return [...this.memory];
    }
    try {
      const value = raw ? JSON.parse(raw) : null;
      this.memory = value?.version === 1 && Array.isArray(value.handles)
        ? [...new Set(value.handles.filter((handle) => this.valid(handle)))].slice(0, 100)
        : [];
    } catch {
      this.memory = [];
    }
    return [...this.memory];
  }

  toggle(handle) {
    if (!this.valid(handle)) return { saved: false, invalid: true };
    const handles = this.read();
    const saved = !handles.includes(handle);
    if (saved && handles.length >= 100) return { saved: false, full: true, persistent: this.persistent };
    this.memory = saved ? [handle, ...handles] : handles.filter((item) => item !== handle);
    try {
      this.getStorage().setItem('drsaviour:wishlist:v1', JSON.stringify({ version: 1, handles: this.memory }));
      this.persistent = true;
      this.memoryOnly = false;
    } catch {
      this.persistent = false;
      this.memoryOnly = true;
    }
    return { saved, persistent: this.persistent };
  }
}

class DrSaviourMobileTabs extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.mobile = window.matchMedia('(max-width: 749px)');
    this.store = new DrSaviourWishlistStore(() => window.localStorage);
    this.dialog = this.querySelector('[data-drs-wishlist-dialog]');
    this.list = this.querySelector('[data-drs-wishlist-list]');
    this.strings = JSON.parse(this.querySelector('[data-drs-mobile-strings]').textContent);
    this.openButton = this.querySelector('[data-drs-wishlist-open]');
    this.openButton.hidden = false;
    this.classList.add('is-ready');
    document.documentElement.classList.add('drs-mobile-tabs-ready');

    document.addEventListener('click', (event) => this.handleSave(event), { capture: true, signal });
    this.addEventListener('click', (event) => this.handleNavigation(event), { signal });
    this.dialog.addEventListener('close', () => {
      this.requests?.abort();
      unlockScroll(this.dialog);
      this.openButton.setAttribute('aria-expanded', 'false');
    }, { signal });
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog && isClickedOutside(event, this.dialog)) this.dialog.close();
    }, { signal });
    this.mobile.addEventListener('change', () => {
      if (!this.mobile.matches && this.dialog.open) this.dialog.close();
      this.syncButtons();
      this.updateKeyboard();
    }, { signal });
    window.addEventListener('storage', (event) => {
      if (event.key !== 'drsaviour:wishlist:v1' && event.key !== null) return;
      this.syncButtons();
      if (this.dialog.open) this.renderWishlist();
    }, { signal });
    window.addEventListener('pageshow', () => this.syncButtons(), { signal });
    window.visualViewport?.addEventListener('resize', () => this.updateKeyboard(), { signal });
    document.addEventListener('focusin', () => this.updateKeyboard(), { signal });
    document.addEventListener('focusout', () => requestAnimationFrame(() => this.updateKeyboard()), { signal });
    this.observer = new MutationObserver(() => this.syncButtons());
    const main = document.getElementById('MainContent');
    if (main) this.observer.observe(main, { childList: true, subtree: true });
    this.syncButtons();
  }

  disconnectedCallback() {
    if (this.dialog?.open) this.dialog.close();
    if (this.dialog) unlockScroll(this.dialog);
    this.controller?.abort();
    this.controller = null;
    this.requests?.abort();
    this.observer?.disconnect();
    clearTimeout(this.toastTimer);
    document.documentElement.classList.remove('drs-mobile-tabs-ready');
  }

  text(key, value) {
    const text = this.strings[key] || '';
    return value === undefined ? text : text.replace('__product__', value).replace('__price__', value);
  }

  syncButtons() {
    if (!this.mobile.matches) return;
    const handles = this.store.read();
    const count = this.querySelector('[data-drs-wishlist-count]');
    count.textContent = String(handles.length);
    count.hidden = handles.length === 0;
    this.querySelector('[data-drs-wishlist-note]').textContent = this.text(this.store.persistent ? 'browser_note' : 'session_note');
    document.querySelectorAll('[data-drs-wishlist-toggle]').forEach((button) => {
      const saved = handles.includes(button.dataset.drsWishlistToggle);
      button.setAttribute('aria-pressed', String(saved));
      button.setAttribute('aria-label', this.text(saved ? 'remove_product' : 'save_product', button.dataset.productTitle || ''));
      const label = button.querySelector('[data-drs-wishlist-text]');
      const text = this.text(saved ? 'saved_action' : 'save_action');
      if (label && label.textContent !== text) label.textContent = text;
    });
  }

  handleSave(event) {
    if (!this.mobile.matches || !(event.target instanceof Element)) return;
    const button = event.target.closest('[data-drs-wishlist-toggle]');
    if (!button) return;
    event.preventDefault();
    event.stopPropagation();
    const result = this.store.toggle(button.dataset.drsWishlistToggle);
    if (result.invalid) return;
    this.syncButtons();
    this.announce(this.text(result.full ? 'full_message' : result.saved ? 'saved_message' : 'removed_message'));
  }

  handleNavigation(event) {
    if (!this.mobile.matches || !(event.target instanceof Element)) return;
    const action = event.target.closest('[data-drs-mobile-action]');
    if (!action || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0) return;
    const type = action.dataset.drsMobileAction;
    if (type === 'wishlist') {
      this.dialog.showModal();
      lockScroll(this.dialog);
      this.openButton.setAttribute('aria-expanded', 'true');
      this.renderWishlist();
    } else if (type === 'close') {
      this.dialog.close();
    } else if (type === 'remove') {
      if (this.store.read().includes(action.dataset.handle)) this.store.toggle(action.dataset.handle);
      this.syncButtons();
      this.renderWishlist();
      this.querySelector('[data-drs-wishlist-close]').focus();
    } else if (type === 'search') {
      const search = document.getElementById('search-modal');
      if (typeof search?.showDialog === 'function') {
        event.preventDefault();
        search.showDialog();
      }
    } else if (type === 'cart') {
      const cart = document.getElementById('cart-drawer');
      if (typeof cart?.open === 'function') {
        event.preventDefault();
        cart.open();
      }
    }
  }

  announce(message) {
    this.querySelector('[data-drs-wishlist-status]').textContent = message;
    const toast = this.querySelector('[data-drs-wishlist-toast]');
    toast.textContent = message;
    toast.hidden = false;
    clearTimeout(this.toastTimer);
    this.toastTimer = setTimeout(() => { toast.hidden = true; }, 2600);
  }

  updateKeyboard() {
    const editing = document.activeElement?.matches('input, textarea, select, [contenteditable="true"]');
    const shortened = window.visualViewport && window.innerHeight - window.visualViewport.height > 150;
    this.toggleAttribute('data-keyboard-open', Boolean(editing && shortened));
  }

  async renderWishlist() {
    this.requests?.abort();
    this.requests = new AbortController();
    const { signal } = this.requests;
    const handles = this.store.read();
    this.querySelector('[data-drs-wishlist-empty]').hidden = handles.length > 0;
    this.querySelector('[data-drs-wishlist-loading]').hidden = handles.length === 0;
    this.list.replaceChildren();
    this.list.setAttribute('aria-busy', String(handles.length > 0));
    for (let index = 0; index < handles.length; index += 4) {
      const results = await Promise.all(handles.slice(index, index + 4).map(async (handle) => {
        try {
          const response = await fetch(`${this.dataset.rootUrl}products/${encodeURIComponent(handle)}.js`, { signal, headers: { Accept: 'application/json' } });
          if (!response.ok) throw new Error('Product unavailable');
          const product = await response.json();
          return { handle, product };
        } catch (error) {
          if (error.name === 'AbortError') return null;
          return { handle, product: null };
        }
      }));
      if (signal.aborted || !this.isConnected) return;
      results.filter(Boolean).forEach(({ handle, product }) => this.list.append(this.productCard(handle, product)));
      this.querySelector('[data-drs-wishlist-loading]').hidden = true;
    }
    this.list.setAttribute('aria-busy', 'false');
  }

  productCard(handle, product) {
    const item = document.createElement('li');
    item.className = 'drs-wishlist__item';
    const info = document.createElement('div');
    info.className = 'drs-wishlist__info';
    const title = document.createElement('a');
    title.className = 'drs-wishlist__product-title';
    title.href = `${this.dataset.rootUrl}products/${encodeURIComponent(handle)}`;
    title.textContent = product?.title || handle.replaceAll('-', ' ');
    info.append(title);
    if (product?.featured_image) {
      const image = document.createElement('img');
      const url = new URL(product.featured_image, document.baseURI);
      if (url.protocol === 'https:') {
        url.searchParams.set('width', '192');
        image.src = url.href;
        image.alt = product.title || '';
        image.width = 96;
        image.height = 96;
        image.loading = 'lazy';
        image.className = 'drs-wishlist__image';
        item.append(image);
      }
    }
    const detail = document.createElement('p');
    detail.className = 'drs-wishlist__price';
    if (product && Number.isFinite(product.price)) {
      const money = new Intl.NumberFormat(document.documentElement.lang || 'en-IN', { style: 'currency', currency: this.dataset.currency || 'INR' }).format(product.price / 100);
      detail.textContent = product.price_varies ? this.text('price_from', money) : money;
      if (!product.available) {
        const stock = document.createElement('span');
        stock.className = 'drs-wishlist__stock';
        stock.textContent = this.text('sold_out');
        info.append(stock);
      }
    } else {
      detail.textContent = this.text('unavailable');
    }
    info.append(detail);
    const actions = document.createElement('div');
    actions.className = 'drs-wishlist__actions';
    if (product) {
      const view = document.createElement('a');
      view.href = title.href;
      view.textContent = this.text('view_product');
      actions.append(view);
    }
    const remove = document.createElement('button');
    remove.type = 'button';
    remove.dataset.drsMobileAction = 'remove';
    remove.dataset.handle = handle;
    remove.textContent = this.text('remove');
    remove.setAttribute('aria-label', this.text('remove_product', product?.title || title.textContent));
    actions.append(remove);
    info.append(actions);
    item.append(info);
    return item;
  }
}

if (!customElements.get('drs-mobile-tabs')) {
  customElements.define('drs-mobile-tabs', DrSaviourMobileTabs);
}
